import { useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from '@/components/ui/Icon';
import { Button } from '@/components/ui/Button';
import { CancelButton } from '@/components/ui/CancelButton';
import { ICONS } from '@/constants/icons';
import { calendarActionsService } from '@/services/calendar-actions.service';
import { toast } from '@/utils/toast';

interface ImportICSDialogProps {
  isOpen: boolean;
  onClose: () => void;
  calendarId: number;
  calendarName: string;
  onSuccess: () => void;
}

export const ImportICSDialog = ({
  isOpen,
  onClose,
  calendarId,
  calendarName,
  onSuccess,
}: ImportICSDialogProps) => {
  const { t } = useTranslation('common');
  const [icsContent, setIcsContent] = useState('');
  const [fileName, setFileName] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  if (!isOpen) return null;

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setFileName(file.name);
      const reader = new FileReader();
      reader.onload = (event) => {
        setIcsContent(event.target?.result as string || '');
      };
      reader.readAsText(file);
    }
  };

  const handleImport = async () => {
    if (!icsContent.trim()) {
      toast.error(t('ics_select_file_error'));
      return;
    }

    setIsLoading(true);
    try {
      const result = await calendarActionsService.importICS(calendarId, icsContent);

      if (result.imported > 0) {
        toast.success(t('ics_imported_events', { count: result.imported }));
        if (result.errors.length > 0) {
          toast.error(t('ics_import_errors', { count: result.errors.length }));
        }
        onSuccess();
        onClose();
      } else {
        toast.error(t('ics_no_events'));
      }
    } catch (error) {
      toast.error(t('ics_import_failed'));
    } finally {
      setIsLoading(false);
    }
  };

  const reset = () => {
    setIcsContent('');
    setFileName('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50" onClick={() => !isLoading && onClose()} />
      <div className="relative bg-white rounded-xl shadow-xl max-w-lg w-full mx-4 p-6">
        {/* Header */}
        <div className="flex items-center gap-3 mb-4">
          <div className="p-2 bg-primary-100 rounded-full">
            <Icon name={ICONS.UPLOAD} size="md" color="text-primary-600" ariaHidden />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-gray-900">{t('import_calendar')}</h2>
            <p className="text-sm text-gray-500">{t('import_to_calendar', { name: calendarName })}</p>
          </div>
        </div>

        {/* File Upload */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            {t('upload_ics_file')}
          </label>
          <div className="flex gap-2">
            <input
              ref={fileInputRef}
              type="file"
              accept=".ics,.ical"
              onChange={handleFileChange}
              className="hidden"
            />
            <Button
              variant="secondary"
              onClick={() => fileInputRef.current?.click()}
              disabled={isLoading}
              className="flex-1"
            >
              {fileName || t('choose_ics_file')}
            </Button>
            {fileName && (
              <Button variant="secondary" onClick={reset} disabled={isLoading}>
                {t('clear')}
              </Button>
            )}
          </div>
        </div>

        {/* Or paste content */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            {t('paste_ics_content')}
          </label>
          <textarea
            value={icsContent}
            onChange={(e) => setIcsContent(e.target.value)}
            placeholder={t('ics_placeholder')}
            rows={6}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm font-mono resize-none"
            disabled={isLoading}
          />
        </div>

        {/* Info */}
        <p className="text-xs text-gray-500 mb-4">
          {t('ics_help_text')}
        </p>

        {/* Actions */}
        <div className="flex justify-end gap-1.5 sm:gap-2">
          <CancelButton onClick={onClose} disabled={isLoading} tooltip="Cancel" />
          <Button variant="primary" onClick={handleImport} disabled={isLoading || !icsContent.trim()}>
            {isLoading ? t('importing') : t('import')}
          </Button>
        </div>
      </div>
    </div>
  );
};
